import React, { useState, useEffect } from 'react';
import { format, addDays, startOfWeek, addWeeks, subWeeks, isSameDay, parseISO } from 'date-fns';
import { bookingsApi } from '../lib/api';
import { Booking } from '../types';
import AdminLayout from '../components/AdminLayout';

type ViewMode = 'day' | 'week';

const Calendar: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);

  const hours = Array.from({ length: 12 }, (_, i) => i + 8); // 8 AM to 7 PM

  useEffect(() => {
    // Check if user is authenticated
    const user = localStorage.getItem('user');
    if (!user) {
      window.location.href = '/login';
      return;
    }
    fetchBookings();
  }, [currentDate, viewMode]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const startDate = viewMode === 'week'
        ? startOfWeek(currentDate, { weekStartsOn: 1 })
        : currentDate;

      const endDate = viewMode === 'week'
        ? addDays(startDate, 6)
        : currentDate;

      const response = await bookingsApi.list({
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd'),
      });

      // Handle different response formats
      const bookingsData = Array.isArray(response.data)
        ? response.data
        : (response.data?.bookings || []);

      setBookings(bookingsData);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInView = () => {
    if (viewMode === 'day') {
      return [currentDate];
    }
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  };

  const getBookingsForDayAndHour = (day: Date, hour: number) => {
    return bookings.filter(booking => {
      const bookingDate = parseISO(booking.starts_at);
      const bookingHour = bookingDate.getHours();
      return isSameDay(bookingDate, day) && bookingHour === hour;
    });
  };

  const handlePrevious = () => {
    if (viewMode === 'day') {
      setCurrentDate(prev => addDays(prev, -1));
    } else {
      setCurrentDate(prev => subWeeks(prev, 1));
    }
  };

  const handleNext = () => {
    if (viewMode === 'day') {
      setCurrentDate(prev => addDays(prev, 1));
    } else {
      setCurrentDate(prev => addWeeks(prev, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const days = getDaysInView();

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-800">Schedule</h1>
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('day')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  viewMode === 'day'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Day
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  viewMode === 'week'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Week
              </button>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={handleToday}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Today
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevious}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-lg font-semibold text-gray-800 min-w-[200px] text-center">
                {viewMode === 'day'
                  ? format(currentDate, 'MMMM d, yyyy')
                  : `${format(days[0], 'MMM d')} - ${format(days[6], 'MMM d, yyyy')}`}
              </span>
              <button
                onClick={handleNext}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-6">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Day Headers */}
          <div className="grid" style={{ gridTemplateColumns: `80px repeat(${days.length}, 1fr)` }}>
            <div className="border-b border-gray-200 p-4"></div>
            {days.map((day, index) => (
              <div
                key={index}
                className="border-b border-l border-gray-200 p-4 text-center"
              >
                <div className="text-sm text-gray-500 font-medium">
                  {format(day, 'EEE')}
                </div>
                <div
                  className={`text-2xl font-bold mt-1 ${
                    isSameDay(day, new Date())
                      ? 'text-purple-600'
                      : 'text-gray-800'
                  }`}
                >
                  {format(day, 'd')}
                </div>
              </div>
            ))}
          </div>

          {/* Time Grid */}
          <div className="relative">
            {hours.map((hour) => (
              <div
                key={hour}
                className="grid"
                style={{ gridTemplateColumns: `80px repeat(${days.length}, 1fr)`, minHeight: '80px' }}
              >
                {/* Time Label */}
                <div className="border-b border-gray-200 p-4 text-right pr-4">
                  <span className="text-sm text-gray-500 font-medium">
                    {`${hour.toString().padStart(2, '0')}:00`}
                  </span>
                </div>

                {/* Day Cells */}
                {days.map((day, dayIndex) => {
                  const dayBookings = getBookingsForDayAndHour(day, hour);

                  return (
                    <div
                      key={dayIndex}
                      className="border-b border-l border-gray-200 p-2 relative hover:bg-gray-50 transition-colors"
                    >
                      {dayBookings.map((booking, bookingIndex) => (
                        <div
                          key={booking.id}
                          className="mb-2 p-2 rounded-lg bg-gradient-to-br from-blue-400 to-blue-500 text-white text-xs cursor-pointer hover:shadow-md transition-shadow"
                          style={{
                            marginLeft: `${bookingIndex * 4}px`,
                          }}
                        >
                          <div className="font-semibold">{booking.client.name}</div>
                          <div className="opacity-90 mt-1">
                            {booking.items[0]?.service.name || 'Service'}
                          </div>
                          <div className="opacity-80 text-xs mt-1">
                            {format(parseISO(booking.starts_at), 'HH:mm')} - {format(parseISO(booking.ends_at), 'HH:mm')}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 shadow-xl">
              <div className="text-gray-600">Loading bookings...</div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Calendar;
